import { StateCreator } from 'zustand'
import { logger, enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import { SharedSlice } from '../../../../../slice'
import { PayClassification } from '$models'
import { payClassificationService } from '$services'

interface EditPayClassificationState {
  payClassification: PayClassification | null
}

export interface EditPayClassificationSlice extends EditPayClassificationState {
  /**
   * Reset PayClassification Slice
   */
  resetEditPayClassificationSlice: () => void

  /**
   * Get Pay Classification By id
   * @param payClassificationId PayClassification id
   */
  getPayClassification: (
    payClassificationId: string
  ) => Promise<PayClassification | null>

  /**
   * Update PayClassification
   * @param payClassification PayClassification to update
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns
   */
  updatePayClassification: (
    payClassification: PayClassification,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>
}

const initialState: EditPayClassificationState = {
  payClassification: null,
}

const createEditPayClassificationSlice: StateCreator<
  EditPayClassificationSlice & SharedSlice,
  [],
  [],
  EditPayClassificationSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetEditPayClassificationSlice: () => set(initialState),

  // State API calls
  getPayClassification: async (
    payClassificationId: string
  ): Promise<PayClassification | null> => {
    try {
      const response = await payClassificationService.getPayClassification(
        payClassificationId
      )

      set(() => ({
        payClassification: response,
      }))

      return response
    } catch (error) {
      logger.error(
        `Failed to retrieve pay classification ${payClassificationId}`,
        error
      )
      set(() => ({
        payClassification: null,
      }))

      exceptionMessageHandler(error, 'Failed to retrieve pay classification')
      return null
    }
  },

  updatePayClassification: async (
    payClassification: PayClassification,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      const updatedPayClassification =
        await payClassificationService.updatePayClassification(
          payClassification
        )

      set(() => ({
        payClassification: updatedPayClassification,
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
      if (!updatedPayClassification) {
        logger.error(`${errorMessage} - updatedPayClassification doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },
})

export default createEditPayClassificationSlice
